<template>
    <div class="qr-container">
        <!-- Indicatore stato offline -->
        <div v-if="isOffline" class="offline-indicator">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="1" y1="1" x2="23" y2="23"></line>
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"></path>
                <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"></path>
                <path d="M10.71 5.05A16 16 0 0 1 22.58 9"></path>
                <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"></path>
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
                <line x1="12" y1="20" x2="12.01" y2="20"></line>
            </svg>
            <span>Sistema Offline</span>
        </div>

        <!-- QR Code -->
        <div class="qr-wrapper">
            <canvas ref="qrCanvas" class="qr-canvas"></canvas>

            <!-- Timer countdown visivo (opzionale) -->
            <!-- <div class="refresh-info">
                <span>Prossimo aggiornamento: {{ formatTime(timeRemaining) }}</span>
            </div> -->
        </div>
    </div>
</template>

<script>
import { genera_codice } from '@/api/api.manager.js'
import QRCode from 'qrcode'

export default {
    name: 'GeneratoreQR',

    data() {
        return {
            qrCode: '',
            isOffline: false,
            refreshInterval: null,
            countdownInterval: null,
            timeRemaining: 30 * 60, // 30 minuti in secondi
            REFRESH_TIME: 30 * 60 * 1000, // 30 minuti in millisecondi
            OFFLINE_CODE: 'OFFLINE|0|SYSTEM_OFFLINE_MODE', // Codice fisso per offline
        }
    },

    mounted() {
        // Genera il primo QR code all'avvio
        this.generateQRCode()

        // Imposta il refresh automatico ogni 30 minuti
        this.startAutoRefresh()

        // Avvia il countdown
        //this.startCountdown()

        // Listener per eventi online/offline
        window.addEventListener('online', this.handleOnline)
        window.addEventListener('offline', this.handleOffline)
    },

    beforeUnmount() {
        // Pulizia degli interval e listener
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval)
        }
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval)
        }
        window.removeEventListener('online', this.handleOnline)
        window.removeEventListener('offline', this.handleOffline)
    },

    methods: {
        async generateQRCode() {
            try {
                // Verifica connessione
                if (!navigator.onLine) {
                    this.isOffline = true
                    this.qrCode = this.OFFLINE_CODE
                    await this.renderQRCode(this.OFFLINE_CODE)
                    return
                }

                // Chiamata API per generare il codice
                const response = await genera_codice()

                // La risposta è già la stringa nel formato: stationId|timestamp|signature
                this.qrCode = response.data || response
                this.isOffline = false

                // Renderizza il QR code
                await this.renderQRCode(this.qrCode)

                // Reset del timer
                this.timeRemaining = 30 * 60

            } catch (error) {
                console.error('Errore nella generazione del QR code:', error)

                // In caso di errore (es. server non raggiungibile), usa codice offline
                this.isOffline = true
                this.qrCode = this.OFFLINE_CODE
                await this.renderQRCode(this.OFFLINE_CODE)

                // Nota: potresti voler inviare una notifica all'admin qui
                this.notifyAdminOffline()
            }
        },

        async renderQRCode(data) {
            try {
                const canvas = this.$refs.qrCanvas
                if (!canvas) return

                await QRCode.toCanvas(canvas, data, {
                    width: 500, // Dimensione adatta per schermo 11"
                    margin: 2,
                    color: {
                        dark: '#000000',
                        light: '#FFFFFF'
                    },
                    errorCorrectionLevel: 'H' // Alta correzione errori
                })
            } catch (error) {
                console.error('Errore nel rendering del QR code:', error)
            }
        },

        startAutoRefresh() {
            this.refreshInterval = setInterval(() => {
                this.generateQRCode()
            }, this.REFRESH_TIME)
        },

        /* startCountdown() {
            this.countdownInterval = setInterval(() => {
                if (this.timeRemaining > 0) {
                    this.timeRemaining--
                } else {
                    this.timeRemaining = 30 * 60
                }
            }, 1000)
        }, */

        handleOnline() {
            // Quando torna online, rigenera immediatamente il codice
            this.isOffline = false
            this.generateQRCode()
        },

        handleOffline() {
            // Quando va offline, mostra il codice di default
            this.isOffline = true
            this.qrCode = this.OFFLINE_CODE
            this.renderQRCode(this.OFFLINE_CODE)
        },

        formatTime(seconds) {
            const mins = Math.floor(seconds / 60)
            const secs = seconds % 60
            return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        },

        notifyAdminOffline() {
            // Qui puoi implementare la logica per inviare notifiche all'amministratore
            // Ad esempio, una chiamata API o un sistema di logging
            console.warn('Sistema offline - notifica amministratore')

            // Esempio di possibile implementazione:
            // this.$emit('offline-notification')
            // oppure una chiamata a un endpoint specifico quando torna online
        }
    }
}
</script>

<style scoped>
.qr-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100vw;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 3rem;
}

.offline-indicator {
    position: absolute;
    top: 2rem;
    right: 2rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #ef4444;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    font-weight: 600;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    animation: pulse 2s infinite;
}

.offline-indicator svg {
    width: 24px;
    height: 24px;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.7;
    }
}

.qr-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
    background: white;
    padding: 3rem;
    border-radius: 1.5rem;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.qr-canvas {
    border: 4px solid #f3f4f6;
    border-radius: 1rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.refresh-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    color: #6b7280;
    font-size: 1.125rem;
    font-weight: 500;
}

.refresh-info span {
    background: #f3f4f6;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
}

/* Responsive per schermi più piccoli */
@media (max-width: 768px) {
    .qr-wrapper {
        padding: 2rem;
    }

    .offline-indicator {
        top: 1rem;
        right: 1rem;
        font-size: 0.875rem;
        padding: 0.5rem 1rem;
    }
}
</style>